import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { RiskBadge } from "@/components/SourceBadge";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  MagnifyingGlass,
  FileText,
  WarningOctagon,
  Vault,
  CheckCircle,
  XCircle,
  ArrowRight,
} from "@phosphor-icons/react";

function StatCard({ icon: Icon, label, value, sub, tone = "default", testid }) {
  const toneCls = tone === "risk" ? "text-red-600" : tone === "safe" ? "text-green-600" : "text-zinc-950";
  return (
    <div data-testid={testid} className="bg-white border border-zinc-200 rounded-sm p-5 h-full">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</span>
        <Icon size={18} weight="duotone" className="text-zinc-400" />
      </div>
      <div className={`font-heading text-3xl font-extrabold mt-3 ${toneCls}`}>{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [reports, setReports] = useState([]);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/reports").then((r) => setReports(r.data.reports.slice(0, 6))).catch(() => {});
  }, []);

  return (
    <AppShell breadcrumb="Cruscotto">
      <div className="max-w-6xl">
        <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
          <div>
            <h1 className="font-heading text-3xl font-extrabold tracking-tight text-zinc-950">Cruscotto</h1>
            <p className="text-sm text-zinc-500 mt-1">Panoramica delle attività di due diligence e integrità dell'audit.</p>
          </div>
          <Button data-testid="dashboard-new-query" onClick={() => navigate("/app/new")}
            className="bg-zinc-950 text-white hover:bg-zinc-800 rounded-sm gap-2">
            <MagnifyingGlass size={16} weight="bold" /> Nuova indagine
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <StatCard testid="stat-reports" icon={FileText} label="Report emessi" value={stats?.total_reports ?? "—"} sub="Totale generati" />
          <StatCard testid="stat-queries" icon={MagnifyingGlass} label="Interrogazioni" value={stats?.total_queries ?? "—"} sub="Registrate nel ledger" />
          <StatCard testid="stat-risk" icon={WarningOctagon} label="Report ad alto rischio" value={stats?.risk_reports ?? "—"} tone={stats?.risk_reports ? "risk" : "default"} sub="Insolvenza / contenzioso" />
          <StatCard testid="stat-ledger" icon={Vault} label="Integrità Ledger" value={stats ? (stats.ledger_intact ? "OK" : "ROTTA") : "—"} tone={stats?.ledger_intact ? "safe" : "risk"} sub={`${stats?.ledger_entries ?? 0} voci concatenate`} />
        </div>

        {/* Ledger integrity banner */}
        {stats && (
          <div data-testid="ledger-integrity-banner"
            className={`flex items-center gap-3 border rounded-sm px-4 py-3 mb-6 ${stats.ledger_intact ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}`}>
            {stats.ledger_intact ? <CheckCircle size={20} weight="fill" className="text-green-600" /> : <XCircle size={20} weight="fill" className="text-red-600" />}
            <div className="text-sm">
              <span className="font-medium text-zinc-900">
                {stats.ledger_intact ? "Catena di audit integra" : "Catena di audit compromessa"}
              </span>
              <span className="text-zinc-500 ml-2 font-mono text-xs">
                SHA-256 hash-chain · {stats.ledger_entries} voci
              </span>
            </div>
          </div>
        )}

        {/* Recent reports */}
        <div className="bg-white border border-zinc-200 rounded-sm">
          <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-200">
            <h2 className="font-heading text-base font-bold text-zinc-950">Report recenti</h2>
            <button onClick={() => navigate("/app/reports")} data-testid="view-all-reports" className="text-xs font-mono uppercase tracking-wide text-zinc-500 hover:text-zinc-900 flex items-center gap-1">
              Tutti <ArrowRight size={13} />
            </button>
          </div>
          {reports.length === 0 ? (
            <div className="p-10 text-center text-sm text-zinc-500">
              Nessun report ancora. Avvia la tua prima indagine di due diligence.
            </div>
          ) : (
            <div className="divide-y divide-zinc-100">
              {reports.map((r) => (
                <button key={r.id} data-testid={`recent-report-${r.id}`} onClick={() => navigate(`/app/report/${r.id}`)}
                  className="w-full flex items-center gap-4 px-5 py-3 hover:bg-zinc-50 transition-colors duration-150 text-left">
                  <div className="w-8 h-8 rounded-sm bg-zinc-100 border border-zinc-200 flex items-center justify-center shrink-0">
                    <FileText size={15} className="text-zinc-500" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-zinc-900 truncate">{r.target_name}</div>
                    <div className="text-xs text-zinc-500 font-mono truncate">
                      {r.entity_type === "corporate" ? "Società" : "Persona"} · {r.business_purpose_label}
                    </div>
                  </div>
                  <RiskBadge level={r.risk_level} />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}
